// Typed thin client for /api endpoints. Always attaches the current
// Supabase access token so server middleware can verify identity.
import { supabase } from "./supabase";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API error ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function authedFetch(input: string, init?: RequestInit) {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers = new Headers(init?.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(input, { ...init, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body);
  }
  return res;
}

export type MeResponse = {
  user: { id: string; email?: string | null };
  approved: boolean;
  roles: Array<{ projectId: string; role: "viewer" | "operator" | "admin" }>;
  god: { active: false } | { active: true; sessionId: string; expiresAt: string };
};

export async function fetchMe(): Promise<MeResponse> {
  const res = await authedFetch("/api/auth/me");
  return (await res.json()) as MeResponse;
}

export type OnboardResponse =
  | { status: "approved"; reason?: string }
  | { status: "pending" };

export async function onboard(input: {
  inviteCode?: string;
  message?: string;
}): Promise<OnboardResponse> {
  const res = await authedFetch("/api/auth/onboard", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return (await res.json()) as OnboardResponse;
}

export async function elevateGod(): Promise<{ sessionId: string; expiresAt: string }> {
  const res = await authedFetch("/api/auth/god/elevate", { method: "POST" });
  return (await res.json()) as { sessionId: string; expiresAt: string };
}

export async function revokeGod(): Promise<void> {
  await authedFetch("/api/auth/god/revoke", { method: "POST" });
}

// ─────────────────────────────────────────────────────────────────────────
// Projects
// ─────────────────────────────────────────────────────────────────────────

export type ProjectSummary = {
  id: string;
  name: string;
  description: string | null;
  color: string;
  icon: string | null;
  archived_at?: string | null;
};

export async function listProjects(): Promise<ProjectSummary[]> {
  const res = await authedFetch("/api/projects");
  const body = (await res.json()) as { projects: ProjectSummary[] };
  return body.projects;
}

export async function listAllProjects(): Promise<ProjectSummary[]> {
  const res = await authedFetch("/api/projects/all");
  const body = (await res.json()) as { projects: ProjectSummary[] };
  return body.projects;
}

export async function createProject(input: {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
}): Promise<ProjectSummary> {
  const res = await authedFetch("/api/projects", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const body = (await res.json()) as { project: ProjectSummary };
  return body.project;
}

export async function archiveProject(id: string): Promise<void> {
  await authedFetch(`/api/projects/${id}/archive`, { method: "POST" });
}

// ─────────────────────────────────────────────────────────────────────────
// Users
// ─────────────────────────────────────────────────────────────────────────

export type PendingUser = {
  userId: string;
  email: string | null;
  message: string | null;
  requestedAt: string;
};

export type ApprovedUser = {
  userId: string;
  email: string | null;
  approvedAt: string;
  note: string | null;
  roles: Array<{ projectId: string; role: string }>;
};

export async function listPendingUsers(): Promise<PendingUser[]> {
  const res = await authedFetch("/api/users/pending");
  const body = (await res.json()) as { pending: PendingUser[] };
  return body.pending;
}

export async function listApprovedUsers(): Promise<ApprovedUser[]> {
  const res = await authedFetch("/api/users/approved");
  const body = (await res.json()) as { users: ApprovedUser[] };
  return body.users;
}

export async function approveUser(
  userId: string,
  input: { role: "viewer" | "operator" | "admin"; projectIds: string[]; note?: string }
): Promise<void> {
  await authedFetch(`/api/users/${userId}/approve`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function rejectUser(userId: string): Promise<void> {
  await authedFetch(`/api/users/${userId}/reject`, { method: "POST" });
}

// ─────────────────────────────────────────────────────────────────────────
// Runners
// ─────────────────────────────────────────────────────────────────────────

export type RunnerSummary = {
  id: string;
  name: string;
  description?: string | null;
  trust_level: "self" | "trusted" | "untrusted";
  capabilities: string[];
  active: boolean;
  last_seen_at: string | null;
};

export async function listRunners(): Promise<RunnerSummary[]> {
  const res = await authedFetch("/api/runners");
  const body = (await res.json()) as { runners: RunnerSummary[] };
  return body.runners;
}

export async function listAllRunners(): Promise<RunnerSummary[]> {
  const res = await authedFetch("/api/runners/all");
  const body = (await res.json()) as { runners: RunnerSummary[] };
  return body.runners;
}

export async function createRunner(input: {
  id: string;
  name: string;
  description?: string;
  trustLevel?: "self" | "trusted" | "untrusted";
  capabilities?: string[];
}): Promise<RunnerSummary> {
  const res = await authedFetch("/api/runners", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const body = (await res.json()) as { runner: RunnerSummary };
  return body.runner;
}

export async function updateRunner(
  id: string,
  patch: Partial<{
    name: string;
    description: string;
    trustLevel: "self" | "trusted" | "untrusted";
    capabilities: string[];
    active: boolean;
  }>
): Promise<RunnerSummary> {
  const res = await authedFetch(`/api/runners/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  const body = (await res.json()) as { runner: RunnerSummary };
  return body.runner;
}

// ─────────────────────────────────────────────────────────────────────────
// Tasks + Trigger + History
// ─────────────────────────────────────────────────────────────────────────

export type TaskSummary = {
  taskId: string;
  displayName: string;
  description: string;
  project: string;
  tier: "safe" | "side-effect" | "destructive" | "god";
  runsOn: { capabilities?: string[]; runners?: string[] } | null;
  minTrust: "self" | "trusted" | "untrusted" | null;
  schema: Record<string, unknown>;
};

export async function listTasks(): Promise<TaskSummary[]> {
  const res = await authedFetch("/api/tasks");
  const body = (await res.json()) as { tasks: TaskSummary[] };
  return body.tasks;
}

export type TriggerResponse = {
  runId: string;
  publicAccessToken: string;
};

export async function triggerTask(input: {
  taskId: string;
  payload: Record<string, unknown>;
  runnerId?: string;
}): Promise<TriggerResponse> {
  const res = await authedFetch("/api/trigger", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return (await res.json()) as TriggerResponse;
}

export type AuditRow = {
  id: string;
  ts: string;
  actor_kind: "user" | "service" | "system";
  actor_id: string;
  actor_email: string | null;
  project: string | null;
  task_id: string | null;
  run_id: string | null;
  runner_id: string | null;
  tier: string | null;
  decision: "allowed" | "denied";
  denial_reason: string | null;
  god_session_id: string | null;
};

export type AuditDetail = AuditRow & {
  payload: unknown;
  result_summary: Record<string, unknown> | null;
  source_ip: string | null;
  run: {
    id: string;
    status: string;
    taskIdentifier?: string;
    createdAt?: string;
    startedAt?: string;
    finishedAt?: string;
    durationMs?: number;
    output?: unknown;
    error?: unknown;
    metadata?: Record<string, unknown>;
  } | null;
};

// ─────────────────────────────────────────────────────────────────────────
// Service tokens
// ─────────────────────────────────────────────────────────────────────────

export type ServiceTokenSummary = {
  id: string;
  name: string;
  display: string;
  projectIds: string[];
  allowedTaskIds: string[] | null;
  allowedTiers: string[];
  rateLimitPerMin: number;
  ownerEmail: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

export type CreatedServiceToken = {
  /** The full token. Shown ONCE on creation. Never persisted client-side. */
  token: string;
  id: string;
  display: string;
  name: string;
  projectIds: string[];
  allowedTaskIds: string[] | null;
  allowedTiers: string[];
  rateLimitPerMin: number;
};

export async function listServiceTokens(): Promise<ServiceTokenSummary[]> {
  const res = await authedFetch("/api/service-tokens");
  const body = (await res.json()) as { tokens: ServiceTokenSummary[] };
  return body.tokens;
}

export async function createServiceToken(input: {
  name: string;
  projectIds: string[];
  allowedTiers: Array<"safe" | "side-effect" | "destructive">;
  allowedTaskIds?: string[];
  rateLimitPerMin?: number;
}): Promise<CreatedServiceToken> {
  const res = await authedFetch("/api/service-tokens", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return (await res.json()) as CreatedServiceToken;
}

export async function revokeServiceToken(id: string): Promise<void> {
  await authedFetch(`/api/service-tokens/${id}/revoke`, { method: "POST" });
}

export async function listHistory(
  params: { project?: string; limit?: number; decision?: "allowed" | "denied" } = {}
): Promise<AuditRow[]> {
  const search = new URLSearchParams();
  if (params.project) search.set("project", params.project);
  if (params.limit) search.set("limit", String(params.limit));
  if (params.decision) search.set("decision", params.decision);
  const qs = search.toString();
  const res = await authedFetch(`/api/history${qs ? `?${qs}` : ""}`);
  const body = (await res.json()) as { rows: AuditRow[] };
  return body.rows;
}

export async function getHistoryDetail(id: string): Promise<AuditDetail> {
  const res = await authedFetch(`/api/history/${id}`);
  const body = (await res.json()) as { row: AuditDetail };
  return body.row;
}
